import React, { useState, useEffect } from 'react';
import { FileCheck, ChevronLeft } from 'lucide-react';
import { InterviewForm } from './InterviewForm';
import { SkillsEvaluation } from './SkillsEvaluation';
import { InterviewReport } from './InterviewReport';
import { InterviewHistory } from './InterviewHistory';
import { getInterviewHistory, addInterviewToHistory, removeInterviewFromHistory } from '../../services/interviewStorage';
import type { Seniority } from '../../services/skillsGenerator';

interface TechnicalInterviewProps {
  onBack: () => void;
}

export interface Skill {
  id: string;
  name: string;
  description: string;
  evaluation: 'not_met' | 'partially_met' | 'fully_met' | 'exceeds' | null;
  notes?: string;
}

export interface Interview {
  date: string;
  position: string;
  seniority: Seniority;
  candidateName: string;
  interviewer: string;
  interviewDate: string;
  skills: Skill[];
  finalNotes: string;
  recommendation: 'approve' | 'reject' | null;
}

const TechnicalInterview: React.FC<TechnicalInterviewProps> = ({ onBack }) => {
  const [step, setStep] = useState<'form' | 'evaluation' | 'report' | 'history'>('history');
  const [currentInterview, setCurrentInterview] = useState<Interview | null>(null);
  const [history, setHistory] = useState(() => getInterviewHistory());

  const handleStartInterview = (
    position: string,
    seniority: Seniority,
    candidateName: string,
    interviewer: string,
    interviewDate: string,
    skills: Skill[]
  ) => {
    const interview: Interview = {
      date: new Date().toISOString(),
      position,
      seniority,
      candidateName,
      interviewer,
      interviewDate,
      skills,
      finalNotes: '',
      recommendation: null,
    };
    setCurrentInterview(interview);
    setStep('evaluation');
  };

  const handleCompleteEvaluation = (
    skills: Skill[],
    finalNotes: string,
    recommendation: 'approve' | 'reject'
  ) => {
    if (!currentInterview) return;

    const completedInterview: Interview = {
      ...currentInterview,
      skills,
      finalNotes,
      recommendation,
    };

    setCurrentInterview(completedInterview);
    addInterviewToHistory(completedInterview);
    setHistory(getInterviewHistory());
    setStep('report');
  };

  const handleViewInterview = (interview: Interview) => {
    setCurrentInterview(interview);
    setStep('report');
  };

  const handleRemoveInterview = (date: string) => {
    removeInterviewFromHistory(date);
    setHistory(getInterviewHistory());
  };

  const handleNewInterview = () => {
    setCurrentInterview(null);
    setStep('form');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={onBack}
              className="text-gray-600 hover:text-gray-800 transition-colors flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Voltar para Toolbox
            </button>
          </div>

          {step === 'history' && (
            <>
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  <FileCheck className="w-12 h-12 text-blue-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Avaliação Técnica
                </h1>
                <p className="text-gray-600">
                  Gerencie e documente entrevistas técnicas de forma eficiente
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <InterviewHistory
                  interviews={history}
                  onNewInterview={handleNewInterview}
                  onViewInterview={handleViewInterview}
                  onRemoveInterview={handleRemoveInterview}
                />
              </div>
            </>
          )}

          {step === 'form' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <InterviewForm onSubmit={handleStartInterview} />
            </div>
          )}

          {step === 'evaluation' && currentInterview && (
            <SkillsEvaluation
              position={currentInterview.position}
              candidateName={currentInterview.candidateName}
              skills={currentInterview.skills}
              onComplete={handleCompleteEvaluation}
              onBack={() => setStep('form')}
            />
          )}

          {step === 'report' && currentInterview && (
            <InterviewReport
              interview={currentInterview}
              onNewInterview={handleNewInterview}
            />
          )}
        </div>
      </div>

      <footer className="py-4 bg-gray-800 text-white text-center print:hidden">
        <p className="text-sm">Desenvolvido pelo Hub de Engenharia da Zallpy</p>
      </footer>
    </div>
  );
};

export default TechnicalInterview;